import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { StudyNotebookLearningActions } from './StudyNotebookLearningActions';
import type { NoteRegion } from './studyNotebookOperationsApi';
import type { getStudyDocument } from './studyApi';
import {
  clearMaterialNoteDraft,
  keepMaterialNoteDraft,
  readMaterialNoteDraft,
} from './studyNoteDraft';
import {
  listStudyNotes,
  readStudyNote,
  saveStudyNote,
  type NoteWrite,
  type StudyNote,
} from './studyNotesApi';

type Material = Awaited<ReturnType<typeof getStudyDocument>>;
type Props = { document: Material; onUnsavedChange: (dirty: boolean) => void };

export function StudyMaterialNotes(props: Props) {
  const owner = useAuthStore(() => getKidSub());
  return owner ? (
    <MaterialNotesForLearner key={`${owner}:${props.document.id}`} {...props} owner={owner} />
  ) : (
    <p className="p-5 text-sm text-slate2">Sign in as a student to add your own records.</p>
  );
}

function MaterialNotesForLearner({ document, owner, onUnsavedChange }: Props & { owner: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const notes = useQuery({
    queryKey: ['study-material-notes', owner, document.id],
    queryFn: () => listStudyNotes({ document_id: document.id, status: 'active' }),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  if (notes.isError)
    return (
      <section className="p-5" aria-label="My records">
        <h3 className="text-lg font-extrabold text-ink">My records</h3>
        <p role="alert" className="mt-3 text-sm text-ink-soft">
          Your records could not be loaded. The original image is still saved.
        </p>
        <button className="btn-pill-secondary mt-4" onClick={() => void notes.refetch()}>
          Retry records
        </button>
      </section>
    );
  if (!notes.isFetchedAfterMount || !notes.data)
    return (
      <p role="status" className="p-5 text-sm text-slate2">
        Opening your records…
      </p>
    );
  const available = notes.data.items.filter((n) => n.available);
  if (notes.data.items.length && !available.length)
    return (
      <p role="alert" className="p-5 text-sm">
        A linked source is unavailable. Open Class notes to resolve it.
      </p>
    );
  const note = available.find((n) => n.id === selected) ?? available[0];
  return (
    <MaterialRecordsEditor
      key={note?.id ?? 'new'}
      document={document}
      note={note}
      owner={owner}
      onUnsavedChange={onUnsavedChange}
      selection={
        available.length > 1 ? (
          <label className="block text-sm font-bold text-ink">
            Linked class notes
            <select
              className="mt-2 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
              value={note?.id}
              onChange={(e) => setSelected(e.target.value)}
            >
              {available.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.title}
                </option>
              ))}
            </select>
          </label>
        ) : undefined
      }
    />
  );
}

export function MaterialRecordsEditor({
  document,
  note,
  owner,
  onUnsavedChange,
  selection,
  autosave = false,
  compact = false,
  region,
}: Props & {
  note?: StudyNote;
  owner: string;
  selection?: React.ReactNode;
  autosave?: boolean;
  compact?: boolean;
  region?: NoteRegion | null;
}) {
  const client = useQueryClient();
  const [id] = useState(() => note?.id ?? crypto.randomUUID());
  const [base, setBase] = useState(note);
  const [draft] = useState(() => (autosave && note ? readMaterialNoteDraft(owner, note.id) : null));
  const [body, setBody] = useState(draft?.body ?? note?.body ?? '');
  const [pending, setPending] = useState<NoteWrite | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(Boolean(draft && draft.base_version !== note?.version));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [textSelection, setTextSelection] = useState<{ from: number; to: number } | null>(null);
  const live = useRef(true);
  const latestSave = useRef<() => Promise<void>>(async () => undefined);
  const dirty = body !== (base?.body ?? '') || Boolean(pending) || saving;
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      onUnsavedChange(false);
    };
  }, [onUnsavedChange]);
  const current = () => live.current && getKidSub() === owner;
  async function save() {
    if (saving || conflict || !current()) return;
    const snapshot: NoteWrite = pending ?? {
      id,
      request_id: crypto.randomUUID(),
      expected_version: base?.version ?? 0,
      title: base?.title ?? document.file_name.slice(0, 200),
      body,
      topic: base?.topic ?? null,
      term_id: base ? base.term_id : (document.term_id ?? null),
      subject_key: base ? base.subject_key : (document.subject?.subject_key ?? null),
      class_date: base?.class_date ?? null,
      status: 'active',
      pages: base?.pages ?? [{ document_id: document.id, rotation: 0 }],
      linked_task_ids: base?.linked_task_ids ?? [],
      linked_artifact_ids: base?.linked_artifact_ids ?? [],
    };
    setPending(snapshot);
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await saveStudyNote(snapshot);
      if (!current()) return;
      setBase({
        ...snapshot,
        status: 'active',
        version: result.version,
        available: true,
        updated_at: new Date().toISOString(),
      });
      setPending(null);
      setNotice(
        // A cloud receipt, rather than a local draft, confirms this save.
        isLocalStudyExperience()
          ? 'Saved in this experience. Resetting or closing this tab clears it.'
          : 'Saved. Your records are available on your other devices.',
      );
      if (autosave) clearMaterialNoteDraft(owner, id);
      // Keep this editor mounted after a new save; list data can refresh on the next opening.
      void client.invalidateQueries({ queryKey: ['study-notes', owner] });
      void client.invalidateQueries({ queryKey: ['study-note', owner, id] });
      void client.invalidateQueries({
        queryKey: ['study-material-notes', owner, document.id],
        refetchType: 'none',
      });
    } catch (failure) {
      if (!current()) return;
      setError(
        failure instanceof Error
          ? failure.message
          : 'Your records could not be saved. Your draft is still here.',
      );
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500
      ) {
        setPending(null);
        if ('code' in failure && failure.code === 'STUDY_NOTE_VERSION_CHANGED') setConflict(true);
      }
    } finally {
      if (current()) setSaving(false);
    }
  }
  async function loadSaved(keepDraft = false) {
    if (saving || !current()) return;
    setSaving(true);
    try {
      const saved = await readStudyNote(id);
      if (!current()) return;
      if (
        !saved.available ||
        saved.status !== 'active' ||
        !saved.pages.some((p) => p.document_id === document.id)
      )
        throw new Error(
          'These records are no longer available for this image. Reopen the material.',
        );
      setBase(saved);
      if (!keepDraft) setBody(saved.body);
      if (!keepDraft && autosave) clearMaterialNoteDraft(owner, id);
      setConflict(false);
      setError('');
      setNotice('Loaded the saved version.');
    } catch (failure) {
      if (current())
        setError(
          failure instanceof Error ? failure.message : 'The saved version could not be loaded.',
        );
    } finally {
      if (current()) setSaving(false);
    }
  }
  latestSave.current = save;
  useEffect(() => {
    if (!autosave || !note || !dirty || saving || pending || conflict || error) return;
    const timer = window.setTimeout(() => void latestSave.current(), 1000);
    return () => window.clearTimeout(timer);
  }, [autosave, note, body, dirty, saving, pending, conflict, error]);
  return (
    <section
      aria-label="My records"
      className={`flex min-h-0 flex-col gap-4 ${compact ? 'pt-4' : 'p-5 sm:p-6'}`}
    >
      {!compact && (
        <div>
          <p className="text-xs font-extrabold uppercase tracking-wide text-brand-mint">
            Private to me
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-ink">My records</h3>
          <p className="mt-2 text-sm text-slate2">
            Add what you understand, questions to ask, and classroom reminders while you read.
          </p>
        </div>
      )}
      <fieldset disabled={dirty || saving || Boolean(pending)}>{selection}</fieldset>
      {base && !compact && (
        <p className="text-xs font-bold text-slate2">
          {base.title}
          {base.pages.length > 1
            ? ` · Shared with ${base.pages.length} photos in these class notes`
            : ''}
        </p>
      )}
      <label className="flex min-h-0 flex-1 flex-col gap-2 text-sm font-bold text-ink">
        My own words
        <textarea
          value={body}
          maxLength={12000}
          disabled={saving || Boolean(pending)}
          onChange={(e) => {
            setBody(e.target.value);
            setTextSelection(null);
            setNotice('');
            setError('');
            if (autosave) keepMaterialNoteDraft(owner, id, e.target.value, base?.version ?? 0);
          }}
          placeholder="What did I learn? What is still unclear?"
          onSelect={(e) => {
            const field = e.currentTarget;
            setTextSelection(
              field.selectionStart < field.selectionEnd
                ? { from: field.selectionStart, to: field.selectionEnd }
                : null,
            );
          }}
          className="min-h-56 w-full flex-1 resize-y rounded-2xl border border-hairline bg-canvas p-4 font-normal leading-relaxed"
        />
      </label>
      <p className="text-xs text-slate2">
        {body.length.toLocaleString()} / 12,000 characters ·{' '}
        {dirty ? 'Not saved yet' : base ? 'Saved' : 'Start your records'}
      </p>
      {error && (
        <p role="alert" className="rounded-xl bg-wash-coral p-3 text-sm text-ink">
          {error}
        </p>
      )}
      {conflict && (
        <div className="rounded-xl bg-wash-sunshine p-3 text-sm text-ink">
          <p>Another device saved a newer version. Your draft is still here.</p>
          <button disabled={saving} className="mt-3 underline" onClick={() => void loadSaved()}>
            Replace my draft with the saved version
          </button>
          <button
            disabled={saving}
            className="mt-3 block underline"
            onClick={() => void loadSaved(true)}
          >
            Keep my draft and apply it to the newer version
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className="rounded-xl bg-wash-mint p-3 text-sm text-ink">
          {notice}
        </p>
      )}
      <button
        className="btn-pill-primary w-full"
        disabled={saving || conflict || (!dirty && !pending)}
        onClick={() => void save()}
      >
        {saving ? 'Saving…' : pending ? 'Retry save' : 'Save records'}
      </button>
      {autosave && base && (
        <StudyNotebookLearningActions
          note={base}
          disabled={dirty || saving || conflict}
          selection={textSelection}
          region={region}
          onChanged={() => void loadSaved()}
        />
      )}
    </section>
  );
}
